import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Alert, AlertDescription } from "@cms/ui/components/alert";
import { LoadingSpinner } from "@cms/ui/components/loading-spinner";
import { PageHeader } from "@cms/ui/components/page-header";
import { SettingsTabs } from "../components/settings-tabs";
import { WeeklyHoursGrid } from "../components/weekly-hours-grid";
import {
  parseOpeningHours,
  rulesToWeek,
  serializeOpeningHours,
  weekToRules,
  type HoursRule,
} from "../lib/opening-hours";
import type { ClientOutletContext } from "../lib/outlet-context";

/**
 * Settings → Opening Hours: the weekly grid editor for the site's opening
 * hours (site_config.opening_hours_json — the footer list and the JSON-LD
 * OpeningHoursSpecification both come from it). Supports multiple windows a
 * day, e.g. a lunch/dinner split.
 */
export function SettingsHoursPage() {
  const { api, refreshStatus, setBreadcrumbs } = useOutletContext<ClientOutletContext>();
  const [rules, setRules] = useState<HoursRule[] | null>(null);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    setBreadcrumbs?.([{ label: "Settings", href: "/settings" }, { label: "Opening Hours" }]);
    return () => setBreadcrumbs?.(null);
  }, [setBreadcrumbs]);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    api.request<{ config: { opening_hours_json?: string | null } | null }>("/tenant/config")
      .then(({ config }) => {
        if (!cancelled) setRules(weekToRules(parseOpeningHours(config?.opening_hours_json)));
      })
      .catch((err: Error) => {
        if (!cancelled) setLoadError(err.message);
      });
    return () => { cancelled = true; };
  }, [api]);

  const save = async () => {
    if (!api || !rules) return;
    for (const rule of rules) {
      if (!rule.start_time || !rule.end_time || rule.end_time <= rule.start_time) {
        setNotice({ ok: false, text: "Every window needs a start time before its end time." });
        return;
      }
    }
    setSaving(true);
    setNotice(null);
    try {
      await api.request("/tenant/config", {
        method: "PUT",
        body: JSON.stringify({ opening_hours_json: serializeOpeningHours(rulesToWeek(rules)) }),
      });
      setNotice({ ok: true, text: "Opening hours saved. Publish the site from the top bar to make them go live." });
      refreshStatus?.();
    } catch (err) {
      setNotice({ ok: false, text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{loadError}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div>
      <SettingsTabs
        action={
          <Button size="sm" onClick={() => { void save(); }} disabled={saving || !rules}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save hours
          </Button>
        }
      />

      <PageHeader title="Opening Hours" />

      {notice && (
        <Alert variant={notice.ok ? "default" : "destructive"} className="mb-4">
          {notice.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <AlertDescription>{notice.text}</AlertDescription>
        </Alert>
      )}

      {!rules ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Opening hours</CardTitle>
            <CardDescription>
              Add a second window to a day for split hours (e.g. lunch and dinner service).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <WeeklyHoursGrid rules={rules} onChange={setRules} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
